import type { SiteTemplateDesign } from "./types";

export type ShopVerticalHeroVariant = "centered" | "editorial" | "product" | "reverse" | "split";

/**
 * Shop intentionally keeps the established commerce navigation catalog.
 * Web-only presentation variants must be opted into separately so they do
 * not silently alter the Laguna editor or persisted shop contracts.
 */
export const SHOP_NAVIGATION_VARIANTS = [
    "departmentMega",
    "audienceMega",
    "editorialOverlay",
    "roomPlanner",
    "routineMega",
    "collectionConcierge",
    "groceryHub",
    "productStory",
    "categoryPills",
    "technicalIndex",
    "lagunaClassic",
    "borderedDropdown",
    "imageCategoryStrip",
    "monochromeDropdown",
    "playfulFrame",
] as const;

/** @deprecated Prefer the neutral SiteNavigationVariant type. */
export type ShopNavigationVariant = (typeof SHOP_NAVIGATION_VARIANTS)[number];

export type ShopVerticalNavigationVariant = Exclude<ShopNavigationVariant, "lagunaClassic">;
export type ShopVerticalInspiration = "editorial" | "utility" | "boutique" | "commerce";
export type ShopVerticalPresentationProfile = ShopVerticalInspiration | "kids";

const EDITORIAL_VERTICAL_COLORS = {
    primary: "#E89016",
    accent: "#111111",
    background: "#FFFFFF",
    surface: "#FFFFFF",
    text: "#111111",
    muted: "#666666",
    border: "#D8D8D8",
} as const;

const BOUTIQUE_VERTICAL_COLORS = {
    primary: "#6883BA",
    accent: "#FDBCB4",
    background: "#F9F9F9",
    surface: "#FFFFFF",
    text: "#111111",
    muted: "#666666",
    border: "#DEDEDE",
} as const;

const COMMERCE_VERTICAL_COLORS = {
    primary: "#111111",
    accent: "#F6C09A",
    background: "#FFFFFF",
    surface: "#FFFFFF",
    text: "#111111",
    muted: "#666666",
    border: "#DADADA",
} as const;

const KIDS_VERTICAL_COLORS = {
    primary: "#6674E8",
    accent: "#57C8C0",
    background: "#FFF6DC",
    surface: "#FFFFFF",
    text: "#27324A",
    muted: "#27324A",
    border: "#DDF5F2",
} as const;

/**
 * Vertical templates may use any modern commerce layout. The preserved
 * Laguna base design remains selectable, but is intentionally not assigned
 * to one of the vertical presets.
 */
export const SHOP_VERTICAL_NAVIGATION_VARIANTS: readonly ShopVerticalNavigationVariant[] = (
    SHOP_NAVIGATION_VARIANTS.filter(
        (variant): variant is ShopVerticalNavigationVariant => variant !== "lagunaClassic",
    )
);

export type ShopVerticalTemplateDescriptor = Readonly<{
    category: string;
    code: string;
    content: Readonly<{
        action: string;
        body: string;
        editorialAlt: string;
        eyebrow: string;
        heroAlt: string;
        storyBody: string;
        storyTitle: string;
        title: string;
    }>;
    defaults: SiteTemplateDesign;
    description: string;
    heroVariant: ShopVerticalHeroVariant;
    inspiration: ShopVerticalInspiration;
    name: string;
    navigationVariant: ShopVerticalNavigationVariant;
    presentationProfile?: ShopVerticalPresentationProfile;
    previewImage: string;
}>;

export const SHOP_VERTICAL_TEMPLATE_DESCRIPTORS = [
    {
        category: "beauty",
        code: "beauty-store",
        name: "Kosmetika a péče o pleť",
        description: "Moderní beauty e-shop postavený kolem účinků, rutin, ingrediencí a srozumitelného výběru péče.",
        previewImage: "/templates/beauty-store/preview.jpg",
        heroVariant: "split",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Space Grotesk, Inter, system-ui, sans-serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1320px", sectionSpacing: "comfortable", radius: "8px" },
            components: { header: "minimal", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "PÉČE PODLE POTŘEBY",
            title: "Rutina, která dává smysl vaší pleti.",
            body: "Vyberte péči podle cíle, textury a ingrediencí. Každý krok je vysvětlený jednoduše a bez přehnaných slibů.",
            action: "Najít svou rutinu",
            storyTitle: "Od ingredience k celému rituálu",
            storyBody: "Přehledné účinky, pořadí kroků a kombinace produktů pomáhají nakoupit s jistotou.",
            heroAlt: "Minimalistická kompozice kosmetických produktů pro každodenní péči",
            editorialAlt: "Detail textur a ingrediencí kosmetické rutiny",
        },
    },
    {
        category: "fashion",
        code: "fashion-store",
        name: "Módní obchod",
        description: "Výrazný módní e-shop s kampaněmi, editovanými kolekcemi, outfity a praktickými informacemi o střihu.",
        previewImage: "/templates/fashion-store/preview.jpg",
        heroVariant: "editorial",
        inspiration: "editorial",
        navigationVariant: "borderedDropdown",
        defaults: {
            colors: EDITORIAL_VERTICAL_COLORS,
            typography: { heading: "Arial Narrow, Space Grotesk, Inter, sans-serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1440px", sectionSpacing: "spacious", radius: "0px" },
            components: { header: "minimal", footer: "columns", buttons: "outline", forms: "minimal", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "NOVÁ EDITACE / 01",
            title: "Silné siluety. Věci, které se opravdu nosí.",
            body: "Kolekce sestavená podle střihu, materiálu a způsobu kombinování — od prvního pohledu až po správnou velikost.",
            action: "Prohlédnout nový drop",
            storyTitle: "Jeden šatník, více způsobů nošení",
            storyBody: "Lookbook, kompletní outfity a informace o fitu propojují inspiraci s rychlým nákupem.",
            heroAlt: "Současná módní editorial fotografie nové kolekce",
            editorialAlt: "Detail materiálu a vrstveného módního outfitu",
        },
    },
    {
        category: "jewelry",
        code: "jewelry-store",
        name: "Šperky a klenoty",
        description: "Galerijní šperkařský e-shop zaměřený na materiál, původ, význam kolekcí, dárky a osobní servis.",
        previewImage: "/templates/jewelry-store/preview.jpg",
        heroVariant: "centered",
        inspiration: "editorial",
        navigationVariant: "borderedDropdown",
        defaults: {
            colors: EDITORIAL_VERTICAL_COLORS,
            typography: { heading: "Cormorant Garamond, Iowan Old Style, Georgia, serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1280px", sectionSpacing: "spacious", radius: "2px" },
            components: { header: "classic", footer: "columns", buttons: "outline", forms: "minimal", cartFlow: "multiStep" },
        },
        content: {
            eyebrow: "SIGNATURE COLLECTION",
            title: "Šperk jako osobní gesto.",
            body: "Čistá prezentace materiálu, rozměrů a řemesla dává každému kusu prostor — a zákazníkovi jistotu při výběru.",
            action: "Objevit kolekci",
            storyTitle: "Detail, původ a péče",
            storyBody: "Příběh výroby, průvodce velikostí a osobní konzultace tvoří přirozenou součást nákupu.",
            heroAlt: "Makro detail ručně zpracovaného šperku na tmavém podkladu",
            editorialAlt: "Šperkař při přesné ruční práci v ateliéru",
        },
    },
    {
        category: "furniture",
        code: "furniture-store",
        name: "Prodejna nábytku",
        description: "Klidný architektonický e-shop pro nábytek podle místnosti, rozměrů, materiálu, dostupnosti a služeb.",
        previewImage: "/templates/furniture-store/preview.jpg",
        heroVariant: "reverse",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Cormorant Garamond, Georgia, serif", body: "DM Sans, Inter, system-ui, sans-serif" },
            layout: { container: "1400px", sectionSpacing: "spacious", radius: "4px" },
            components: { header: "classic", footer: "columns", buttons: "solid", forms: "line", cartFlow: "multiStep" },
        },
        content: {
            eyebrow: "MÍSTNOST / MATERIÁL / FUNKCE",
            title: "Nábytek, který drží prostor pohromadě.",
            body: "Vybírejte podle místnosti, rozměru a materiálu. Dostupnost, doprava i montáž jsou vidět dřív, než se rozhodnete.",
            action: "Zařídit místnost",
            storyTitle: "Celá místnost, ne jen jeden kus",
            storyBody: "Scény, kompatibilní produkty a přesné rozměry pomáhají navrhnout promyšlený celek.",
            heroAlt: "Vzdušný moderní interiér s nadčasovým nábytkem",
            editorialAlt: "Detail přírodního materiálu a řemeslného spoje nábytku",
        },
    },
    {
        category: "home-decor",
        code: "home-decor-store",
        name: "Bytové doplňky a dekorace",
        description: "Magazínový e-shop s bytovými doplňky podle místnosti, nálady, barvy, kolekce a dárkové příležitosti.",
        previewImage: "/templates/home-decor-store/preview.jpg",
        heroVariant: "editorial",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Bodoni Moda, Didot, Georgia, serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1360px", sectionSpacing: "spacious", radius: "2px" },
            components: { header: "minimal", footer: "columns", buttons: "outline", forms: "minimal", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "THE HOME EDIT",
            title: "Malé věci mění rytmus celého domova.",
            body: "Novinky, sezónní kolekce a výběry podle nálady propojují inspiraci s konkrétními produkty.",
            action: "Prohlédnout nový edit",
            storyTitle: "Kolekce poskládané jako editoriál",
            storyBody: "Barva, materiál a měřítko vedou zákazníka od jedné dekorace k harmonickému celku.",
            heroAlt: "Editorální zátiší s moderními bytovými doplňky",
            editorialAlt: "Detail prostřeného stolu a vrstvených bytových textilií",
        },
    },
    {
        category: "pets",
        code: "pet-store",
        name: "Chovatelské potřeby",
        description: "Přátelský e-shop s výběrem podle zvířete, věku, velikosti, denní rutiny, péče a výhodných setů.",
        previewImage: "/templates/pet-store/preview.jpg",
        heroVariant: "product",
        inspiration: "commerce",
        navigationVariant: "monochromeDropdown",
        defaults: {
            colors: COMMERCE_VERTICAL_COLORS,
            typography: { heading: "Poppins, Inter, system-ui, sans-serif", body: "Poppins, Inter, system-ui, sans-serif" },
            layout: { container: "1392px", sectionSpacing: "comfortable", radius: "0px" },
            components: { header: "mega", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "PRO KOHO DNES NAKUPUJETE?",
            title: "Každý den se počítá. Procházka, hra i klid.",
            body: "Vyberte podle zvířete a skutečné potřeby. Velikosti, použití a vhodné sety jsou jasné už při procházení.",
            action: "Vybrat podle mazlíčka",
            storyTitle: "Výbava podle každodenní rutiny",
            storyBody: "Krmivo, hra, procházka a péče jsou propojené tak, aby šel správný produkt najít rychle.",
            heroAlt: "Spokojený pes a kočka s moderními chovatelskými potřebami",
            editorialAlt: "Detail odolného vodítka, misky a hraček pro mazlíčky",
        },
    },
    {
        category: "sports",
        code: "sports-store",
        name: "Sport a outdoor",
        description: "Dynamický sportovní e-shop podle disciplíny, terénu, intenzity, počasí, fitu a kompletní výbavy.",
        previewImage: "/templates/sports-store/preview.jpg",
        heroVariant: "reverse",
        inspiration: "editorial",
        navigationVariant: "borderedDropdown",
        defaults: {
            colors: EDITORIAL_VERTICAL_COLORS,
            typography: { heading: "Barlow Condensed, Arial Narrow, sans-serif", body: "Space Grotesk, Inter, system-ui, sans-serif" },
            layout: { container: "1440px", sectionSpacing: "comfortable", radius: "0px" },
            components: { header: "mega", footer: "columns", buttons: "solid", forms: "line", cartFlow: "multiStep" },
        },
        content: {
            eyebrow: "READY FOR THE NEXT KM",
            title: "Výbava, která pracuje ve stejném tempu jako vy.",
            body: "Sport, terén a podmínky jsou důležitější než nekonečný seznam produktů. Začněte tím, kde a jak se hýbete.",
            action: "Vybrat svůj sport",
            storyTitle: "Technologie přeložená do výkonu",
            storyBody: "Fit, materiál a konkrétní použití vysvětlujeme tak, aby šla kompletní výbava sestavit bez hádání.",
            heroAlt: "Sportovec v pohybu s moderním outdoorovým vybavením",
            editorialAlt: "Technický detail sportovní obuvi a funkčního materiálu",
        },
    },
    {
        category: "baby",
        code: "baby-store",
        name: "Výbava pro miminka a děti",
        description: "Klidný rodičovský e-shop organizovaný podle věku, životní fáze, rutiny, bezpečnosti a startovacích sad.",
        previewImage: "/templates/baby-store/preview.jpg",
        heroVariant: "split",
        inspiration: "commerce",
        navigationVariant: "playfulFrame",
        presentationProfile: "kids",
        defaults: {
            colors: KIDS_VERTICAL_COLORS,
            typography: {
                heading: "ui-rounded, 'Arial Rounded MT Bold', 'Trebuchet MS', system-ui, sans-serif",
                body: "'Trebuchet MS', system-ui, sans-serif",
            },
            layout: { container: "1300px", sectionSpacing: "comfortable", radius: "24px" },
            components: { header: "classic", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "HRANÍ, TVOŘENÍ A OBJEVOVÁNÍ",
            title: "Barevný svět pro malé objevitele.",
            body: "Hračky, tvořivé sady a dětská výbava vybrané podle věku, zájmů a společných dobrodružství.",
            action: "Objevit hravý svět",
            storyTitle: "Každý den přináší nový nápad ke hře",
            storyBody: "Přehledné výběry propojují pohyb, fantazii, učení i klidné chvíle, aby si každé dítě našlo svou oblíbenou zábavu.",
            heroAlt: "Veselé dítě objevující barevný svět her a tvoření",
            editorialAlt: "Barevné hračky a tvořivé potřeby připravené pro dětské dobrodružství",
        },
    },
    {
        category: "food",
        code: "food-store",
        name: "Obchod s potravinami",
        description: "Čerstvý online market s nákupem podle sezóny, jídla, původu, dietních potřeb a termínu doručení.",
        previewImage: "/templates/food-store/preview.jpg",
        heroVariant: "editorial",
        inspiration: "commerce",
        navigationVariant: "monochromeDropdown",
        defaults: {
            colors: COMMERCE_VERTICAL_COLORS,
            typography: { heading: "Poppins, Inter, system-ui, sans-serif", body: "Poppins, Inter, system-ui, sans-serif" },
            layout: { container: "1392px", sectionSpacing: "comfortable", radius: "0px" },
            components: { header: "mega", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "PRÁVĚ V SEZÓNĚ",
            title: "Dobré jídlo začíná tím, odkud přichází.",
            body: "Čerstvé suroviny, zásoby do spíže a řešení pro dnešní večeři — s jasným původem i podmínkami doručení.",
            action: "Nakoupit sezonní výběr",
            storyTitle: "Od producenta rovnou do košíku",
            storyBody: "Původ, čerstvost, alergeny a tipy na přípravu zůstávají propojené s konkrétním produktem.",
            heroAlt: "Čerstvá sezonní zelenina a pečivo v moderním farmářském marketu",
            editorialAlt: "Producent připravující čerstvé potraviny k doručení",
        },
    },
    {
        category: "coffee",
        code: "coffee-store",
        name: "Pražírna a obchod s kávou",
        description: "Výrazný kávový e-shop podle chuti, původu, zpracování, způsobu přípravy a pravidelného odběru.",
        previewImage: "/templates/coffee-store/preview.jpg",
        heroVariant: "centered",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Space Grotesk, Inter, sans-serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1280px", sectionSpacing: "spacious", radius: "6px" },
            components: { header: "minimal", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "CURRENT ROAST / 04",
            title: "Najděte kávu podle chuti, ne podle obalu.",
            body: "Původ, zpracování, pražení a metoda přípravy tvoří jeden srozumitelný chuťový profil.",
            action: "Projít chuťový profil",
            storyTitle: "Každá sklizeň má svůj záznam",
            storyBody: "Transparentní původ, recept a čerstvost propojují pražírnu s každým šálkem.",
            heroAlt: "Čerstvě pražená výběrová káva a minimalistické balení bez značky",
            editorialAlt: "Barista připravující filtrovanou kávu s přesným receptem",
        },
    },
    {
        category: "wellness",
        code: "wellness-supplements",
        name: "Doplňky stravy a wellness",
        description: "Klidný evidence-first e-shop s výběrem podle cíle, rutiny, složení, testování a bezpečného užívání.",
        previewImage: "/templates/wellness-supplements/preview.jpg",
        heroVariant: "product",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Manrope, Inter, system-ui, sans-serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1280px", sectionSpacing: "spacious", radius: "6px" },
            components: { header: "classic", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "RUTINA / SLOŽENÍ / DŮKAZY",
            title: "Wellness bez mlhy kolem etikety.",
            body: "Přesné složení, dávkování, testování a vhodnost pro konkrétní cíl vysvětlujeme bez léčebných slibů.",
            action: "Sestavit rutinu",
            storyTitle: "Každá složka má dohledatelný důvod",
            storyBody: "Původ, množství, certifikace a bezpečnostní informace jsou dostupné dřív než nákupní tlačítko.",
            heroAlt: "Čistá kompozice wellness doplňků s transparentním složením",
            editorialAlt: "Laboratorně přesné suroviny a kontrola kvality doplňků stravy",
        },
    },
    {
        category: "electronics",
        code: "electronics-store",
        name: "Elektronika a chytrá zařízení",
        description: "Technický e-shop s produktovými rodinami, použitím, parametry, kompatibilitou, příslušenstvím a podporou.",
        previewImage: "/templates/electronics-store/preview.jpg",
        heroVariant: "product",
        inspiration: "commerce",
        navigationVariant: "monochromeDropdown",
        defaults: {
            colors: COMMERCE_VERTICAL_COLORS,
            typography: { heading: "Poppins, Inter, system-ui, sans-serif", body: "Poppins, Inter, system-ui, sans-serif" },
            layout: { container: "1392px", sectionSpacing: "comfortable", radius: "0px" },
            components: { header: "mega", footer: "columns", buttons: "solid", forms: "minimal", cartFlow: "multiStep" },
        },
        content: {
            eyebrow: "NEW DEVICE / CONNECTED",
            title: "Technologie, která zapadá do celého ekosystému.",
            body: "Porovnejte hlavní parametry, použití a kompatibilitu bez marketingového šumu. Příslušenství i podpora navazují přirozeně.",
            action: "Vybrat zařízení",
            storyTitle: "Parametry převedené do běžného použití",
            storyBody: "Výdrž, výkon, připojení a servis ukazujeme ve chvíli, kdy skutečně pomáhají rozhodnout.",
            heroAlt: "Moderní chytré zařízení v precizní technické produktové scéně",
            editorialAlt: "Detail konstrukce a modulárních součástí elektronického zařízení",
        },
    },
    {
        category: "single-product",
        code: "one-product-store",
        name: "E-shop s jedním produktem",
        description: "Soustředěná produktová šablona pro jeden hlavní výrobek, varianty, demonstraci, důkazy a podporu.",
        previewImage: "/templates/one-product-store/preview.jpg",
        heroVariant: "centered",
        inspiration: "editorial",
        navigationVariant: "borderedDropdown",
        defaults: {
            colors: EDITORIAL_VERTICAL_COLORS,
            typography: { heading: "Sora, Inter, system-ui, sans-serif", body: "Manrope, Inter, system-ui, sans-serif" },
            layout: { container: "1320px", sectionSpacing: "spacious", radius: "12px" },
            components: { header: "minimal", footer: "columns", buttons: "solid", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "ONE PRODUCT / MANY DAYS",
            title: "Čerstvý nápoj. Kdekoli. Bez kabelu.",
            body: "Přenosný mixér ukazujeme v reálném použití: výkon, výdrž, čištění, varianty i to, co přesně najdete v balení.",
            action: "Vybrat svou variantu",
            storyTitle: "Jeden problém, promyšlené řešení",
            storyBody: "Demonstrace, srovnání a jasné parametry vedou k nákupu bez zbytečného katalogového šumu.",
            heroAlt: "Prémiový bezdrátový přenosný mixér v čisté produktové scéně",
            editorialAlt: "Přenosný mixér při přípravě čerstvého nápoje mimo domov",
        },
    },
    {
        category: "handmade",
        code: "handmade-store",
        name: "Ručně vyráběné produkty",
        description: "Hmatový marketplace pro malé série, autory, materiály, původ, personalizaci a poctivou výrobu.",
        previewImage: "/templates/handmade-store/preview.jpg",
        heroVariant: "reverse",
        inspiration: "boutique",
        navigationVariant: "imageCategoryStrip",
        defaults: {
            colors: BOUTIQUE_VERTICAL_COLORS,
            typography: { heading: "Roboto Slab, Georgia, serif", body: "Inter, system-ui, sans-serif" },
            layout: { container: "1280px", sectionSpacing: "comfortable", radius: "3px" },
            components: { header: "classic", footer: "columns", buttons: "outline", forms: "line", cartFlow: "singlePage" },
        },
        content: {
            eyebrow: "MALÁ SÉRIE / RUČNÍ PRÁCE",
            title: "Věci, na kterých je vidět ruka autora.",
            body: "Materiál, původ, přirozené odchylky a čas výroby jsou součástí produktu — ne poznámka schovaná na konci.",
            action: "Objevit nové kusy",
            storyTitle: "Od dílny až k bezpečnému balení",
            storyBody: "Proces, profil autora a péče o výrobek dávají každému malému dropu skutečný kontext.",
            heroAlt: "Řemeslník pracující v současné keramické a dřevěné dílně",
            editorialAlt: "Detail ruční výroby a autentických přírodních materiálů",
        },
    },
    {
        category: "wholesale",
        code: "b2b-wholesale",
        name: "Velkoobchod a B2B katalog",
        description: "Přesný velkoobchodní katalog s dominantním hledáním, technickými kategoriemi, rychlou objednávkou a podporou.",
        previewImage: "/templates/b2b-wholesale/preview.jpg",
        heroVariant: "split",
        inspiration: "commerce",
        navigationVariant: "monochromeDropdown",
        defaults: {
            colors: COMMERCE_VERTICAL_COLORS,
            typography: { heading: "Poppins, Inter, system-ui, sans-serif", body: "Poppins, Inter, system-ui, sans-serif" },
            layout: { container: "1392px", sectionSpacing: "compact", radius: "0px" },
            components: { header: "mega", footer: "columns", buttons: "solid", forms: "line", cartFlow: "multiStep" },
        },
        content: {
            eyebrow: "SKU / PARAMETR / DOSTUPNOST",
            title: "Nákupní pult pro lidi, kteří přesně vědí, co potřebují.",
            body: "Vyhledávání podle názvu, dílu nebo kódu vede rovnou k technickým údajům, balení a dostupnosti.",
            action: "Otevřít technický katalog",
            storyTitle: "Rychlá objednávka bez zbytečných kroků",
            storyBody: "Kompaktní výpisy, množství, dokumenty a kontakt na podporu drží nákupní proces pohromadě.",
            heroAlt: "Přesně uspořádaný profesionální velkoobchodní sklad a technické díly",
            editorialAlt: "Detail označených technických součástek a profesionální expedice",
        },
    },
] as const satisfies readonly ShopVerticalTemplateDescriptor[];

export const SHOP_VERTICAL_TEMPLATE_CODES = SHOP_VERTICAL_TEMPLATE_DESCRIPTORS.map(({ code }) => code);

export function findShopVerticalTemplateDescriptor(code: string | null | undefined): ShopVerticalTemplateDescriptor | null {
    const normalized = code?.trim().toLowerCase();

    return SHOP_VERTICAL_TEMPLATE_DESCRIPTORS.find((descriptor) => descriptor.code === normalized) ?? null;
}
